import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import api, { money } from "../../lib/api";
import { Loading, EmptyState } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StageBar";
import { toast } from "sonner";
import { OfficeEmailStatus } from "../../components/OfficeEmailStatus";
import { FileText, Check, X, Mail, Phone, MapPin, Calendar } from "lucide-react";

export default function OwnerQuotes() {
  const { data, loading, refetch } = useFetch("/quotes");
  const [active, setActive] = useState(null);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [note, setNote] = useState("");

  if (loading) return <Loading label="Loading Smart Quote requests" />;
  const list = data || [];

  const openApprove = (q) => { setActive(q); setLow(q.preliminary_low); setHigh(q.preliminary_high); setNote(""); };

  const approve = async () => {
    try {
      const { data } = await api.post(`/quotes/${active.id}/approve`, { final_low: Number(low), final_high: Number(high), office_note: note, origin_url: window.location.origin });
      if (data.email_sent) toast.success("Estimate approved; notification email accepted for delivery");
      else toast.warning("Estimate saved, but email delivery failed. Contact the customer directly.");
      setActive(null); refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };

  const decline = async (q) => {
    try { await api.post(`/quotes/${q.id}/decline`, { office_note: "" }); toast.success("Quote declined"); refetch(); }
    catch { toast.error("Could not decline"); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Smart Quote" title="Smart Quotes" subtitle={`${list.filter((q) => q.status === "new").length} new requests awaiting review.`} />
      {list.length === 0 ? (
        <EmptyState title="No quote requests yet" subtitle="Submissions from the public Smart Quote form appear here." icon={FileText} />
      ) : (
        <div className="space-y-4">
          {list.map((q) => (
            <div key={q.id} className="hw-card p-6 hw-rise" data-testid={`quote-${q.id}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-head text-xl font-bold text-white">{q.category}</h3>
                    <StatusPill status={q.status} />
                  </div>
                  {q.reference && <div className="mt-1 flex flex-wrap items-center gap-3 font-mono text-xs text-slate-400" data-testid={`quote-ref-${q.id}`}><span>{q.reference}{q.town ? ` · ${q.town}` : ''} · Customer email: {q.customer_email_status || 'n/a'}</span><OfficeEmailStatus kind="quotes" record={q} onDone={refetch} /></div>}
                  <p className="mt-2 text-slate-300">{q.description || "No description provided."}</p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-400">
                    <span className="flex items-center gap-1.5"><Mail size={14} /> {q.email}</span>
                    {q.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {q.phone}</span>}
                    {q.address && <span className="flex items-center gap-1.5"><MapPin size={14} /> {q.address}</span>}
                    {q.preferred_date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {q.preferred_date} · {q.preferred_window}</span>}
                  </div>
                  {q.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      {q.photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 rounded-lg border border-slate-700 object-cover" />)}
                    </div>
                  )}
                  <div className="mt-3 text-sm">
                    <span className="hw-eyebrow">Preliminary</span>{" "}
                    <span className="font-head text-lg font-bold text-cyan-400">{money(q.preliminary_low)}–{money(q.preliminary_high)}</span>
                    {q.status === "approved" && (
                      <div className="mt-1 text-emerald-300">Approved {money(q.final_low)}–{money(q.final_high)} by {q.approved_by}</div>
                    )}
                  </div>
                </div>
                {q.status === "new" && (
                  <div className="flex gap-2">
                    <button onClick={() => openApprove(q)} className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 font-semibold text-slate-900 hover:bg-emerald-400" data-testid={`approve-quote-${q.id}`}><Check size={16} /> Approve estimate</button>
                    <button onClick={() => decline(q)} className="flex items-center gap-2 rounded-xl border border-slate-600 px-4 py-2.5 font-semibold text-slate-300 hover:border-rose-500/40 hover:text-rose-300" data-testid={`decline-quote-${q.id}`}><X size={16} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => setActive(null)}>
          <div className="hw-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="approve-modal">
            <h3 className="font-head text-2xl font-bold text-white">Approve & send estimate</h3>
            <p className="mt-1 text-sm text-slate-400">{active.category} · {active.name}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="hw-eyebrow">Final low</label><input type="number" className="hw-input mt-1" value={low} onChange={(e) => setLow(e.target.value)} data-testid="final-low" /></div>
              <div><label className="hw-eyebrow">Final high</label><input type="number" className="hw-input mt-1" value={high} onChange={(e) => setHigh(e.target.value)} data-testid="final-high" /></div>
            </div>
            <textarea className="hw-input mt-3" rows={3} placeholder="Note to customer (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="approve-note" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setActive(null)} className="rounded-xl border border-slate-600 px-5 py-2.5 font-semibold text-slate-300">Cancel</button>
              <button onClick={approve} className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 font-semibold text-slate-900 hover:bg-emerald-400" data-testid="confirm-approve"><Check size={16} /> Approve & notify</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
